import { useTranslation } from "react-i18next";

import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { type EntityId, type ExchangeLocationType, type TraderOrganization } from "../../../types";

export interface TraderTypeAssignment {
  id: EntityId;
  list: EntityId[];
}

interface TraderTypeMatrixProps {
  exchangeLocationTypes: ExchangeLocationType[];
  isDisabled?: boolean;
  onChange: (assignments: TraderTypeAssignment[]) => void;
  traders: TraderOrganization[];
  value: TraderTypeAssignment[];
}

const isSameId = (first: EntityId, second: EntityId) => String(first) === String(second);

export const toggleAssignment = (
  assignments: TraderTypeAssignment[],
  traderId: EntityId,
  typeId: EntityId
): TraderTypeAssignment[] => {
  const current = assignments.find(assignment => isSameId(assignment.id, traderId));

  if (!current) {
    return [...assignments, { id: traderId, list: [typeId] }];
  }

  const list = current.list.some(type => isSameId(type, typeId))
    ? current.list.filter(type => !isSameId(type, typeId))
    : [...current.list, typeId];

  return assignments.flatMap(assignment =>
    isSameId(assignment.id, traderId)
      ? list.length
        ? [{ ...assignment, list }]
        : []
      : [assignment]
  );
};

export const TraderTypeMatrix = ({
  exchangeLocationTypes,
  isDisabled = false,
  onChange,
  traders,
  value,
}: TraderTypeMatrixProps) => {
  const { t } = useTranslation();

  const isChecked = (traderId: EntityId, typeId: EntityId) =>
    value.some(
      assignment =>
        isSameId(assignment.id, traderId) && assignment.list.some(type => isSameId(type, typeId))
    );

  return (
    <div className="overflow-hidden rounded-md border border-border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="px-3">
              {t("viewExchangeDetailsModal.linkedTraders.table.traderName")}
            </TableHead>
            {exchangeLocationTypes.map(exchangeLocationType => (
              <TableHead
                key={String(exchangeLocationType.id)}
                className="max-w-32 px-3 text-center"
              >
                {exchangeLocationType.name}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {traders.map(trader => (
            <TableRow key={String(trader.id)}>
              <TableCell className="min-w-40 whitespace-normal px-3">{trader.name}</TableCell>
              {exchangeLocationTypes.map(exchangeLocationType => (
                <TableCell key={String(exchangeLocationType.id)} className="px-3 text-center">
                  <Checkbox
                    checked={isChecked(trader.id ?? "", exchangeLocationType.id ?? "")}
                    disabled={isDisabled}
                    aria-label={`${trader.name} – ${exchangeLocationType.name}`}
                    onCheckedChange={() =>
                      onChange(
                        toggleAssignment(value, trader.id ?? "", exchangeLocationType.id ?? "")
                      )
                    }
                  />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
